import { Access, EveryoneAccess } from "../contracts/accessContract";
import { isNil } from "lodash";
import { GroupsService } from "./groupsService";

interface InheritedAccessContract {
    type: "inherited",
    allow: string[];
}

type AccessMap = Record<string, Access>;

interface ImageRegistrationEntry {
    pageUrl: string,
    parentsWithAccess: string[]
}

type ImageRegistrationMap = Record<string, ImageRegistrationEntry[]>;

export interface AccessFileContent {
    urls: Record<string, Access | InheritedAccessContract>;
    elements: AccessMap;
    affectedUrls: string[];
}


export class AccessRegistry {
    private elements: AccessMap = {};
    private pages: AccessMap = {};
    private affectedUrls = new Set<string>();
    private images: ImageRegistrationMap = {};

    constructor(private groupsService: GroupsService) { }

    public registerElement(id: string, access: Access) {
        this.assignAccess(this.elements, id, access);
    }

    public registerPage(url: string, access: Access) {
        this.assignAccess(this.pages, url, access);
    }

    public registerImage(imageUrl: string, pageUrl: string, accessIdsOfParents: string[]) {
        const imageEntry = this.images[imageUrl] ?? (this.images[imageUrl] = []);
        const parentsWithAccess = accessIdsOfParents.filter(this.hasElementWithAccess);
        imageEntry.push({ pageUrl, parentsWithAccess });
    }

    public registerAffectedUrl(url: string, elementAccessId: string) {
        if (!isNil(this.elements[elementAccessId])) {
            this.affectedUrls.add(url);
        }
    }

    public async serialize(): Promise<AccessFileContent> {
        const guestGroupKey = (await this.groupsService.guestGroup()).key;
        const pages = Object.entries(this.pages)
            .map(([url, access]) => [url, this.modifyAccess(guestGroupKey, access)]);
        const elements = Object.entries(this.elements)
            .map(([id, access]) => [id, this.modifyAccess(guestGroupKey, access)]);

        const accessObject: AccessFileContent = {
            urls: Object.fromEntries(pages),
            elements: Object.fromEntries(elements),
            affectedUrls: Array.from(this.affectedUrls)
        };

        this.serializeImageRegistry(accessObject);

        return accessObject;
    }

    private modifyAccess(guestGroupKey: string, access: Access) {
        if (access.type !== "group") {
            return access;
        }

        // replace guest group id with "guests" string
        const newAllow = [...access.allow];
        const guestGroupIndex = access.allow.findIndex(group => group === guestGroupKey);

        if (guestGroupIndex > -1) {
            newAllow.splice(guestGroupIndex, 1, "guests");
        }

        return {
            type: access.type,
            allow: newAllow,
        };
    }

    private assignAccess(map: AccessMap, id: string, access: Access) {
        if (access === EveryoneAccess || access === null || !access.type || !(access.allow?.length > 0)) {
            return;
        }

        if (!isNil(map[id])) {
            throw Error(`Key '${id}' exist in the registry`);
        }

        map[id] = access;
    }

    private serializeImageRegistry(accessObject: AccessFileContent) {
        for (const [imageUrl, imageInPages] of Object.entries(this.images)) {
            this.serializeImage(accessObject, imageUrl, imageInPages);
        }
    }

    private serializeImage(accessObject: AccessFileContent, imageUrl: string, pages: ImageRegistrationEntry[]) {
        const allow = new Set<string>();
        
        for (const page of pages) {
            const containedInPageAccess = this.pages[page.pageUrl];

            if (containedInPageAccess === EveryoneAccess && page.parentsWithAccess.length === 0) {
                return;
            }

            if (containedInPageAccess !== EveryoneAccess) {
                allow.add(page.pageUrl);
            }

            if (page.parentsWithAccess.length > 0) {
                page.parentsWithAccess.forEach(id => allow.add(id));
            }
        }

        accessObject.urls[imageUrl] = {
            type: "inherited",
            allow: Array.from(allow)
        };
    }

    private hasElementWithAccess = (accessId: string) => this.elements[accessId] !== EveryoneAccess;
}